import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { styles } from "./styles";
import {
  Button,
  Card,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  withStyles,
} from "@material-ui/core";
import CheckBox from "../../../../../../../Component/Forms/Checkbox";
import {
  getDataAugmentation,
  importExternalDocuments,
} from "../../../../../../../API/Entity";
import ModalDialogContext from "../../../../../../../Context/ModalDialog/ModalDialogContext";
import LightTooltip from "../../../../../../../Component/LightTooltip";
import { recordTypes } from "../../../../../../../API/config";
import {
  NoAccessScreen,
  LoadingIndicator,
} from "../../../../../../../Component";

const DocumentsTable = (props) => {
  const {
    entity,
    sources,
    availableSources,
    hasRecordPermission,
    reloadParent,
    classes,
  } = props;

  const { t } = useTranslation();

  const { showMessage, showErrorMessage, showConfirmation } =
    useContext(ModalDialogContext);

  const [loading, setLoading] = useState(false);
  const documentTblCols = [
    {
      field: "title",
      title: t("labels.record.augmentation.table.documents.headers.title"),
    },
    {
      field: "category",
      title: t("labels.record.augmentation.table.documents.headers.category"),
    },
    {
      field: "date",
      title: t("labels.record.augmentation.table.documents.headers.date"),
    },
    {
      field: "sourceName",
      title: t("labels.record.augmentation.table.documents.headers.sourceName"),
    },
  ];
  const [isAllDocsSelected, setIsAllDocsSelected] = useState(false);
  const [documentTblRows, setDocumentTblRows] = useState([]);
  const [isOperating, setIsOperating] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const setAugmentationData = (augmentationData) => {
      setDocumentTblRows([]);
      Object.keys(augmentationData).forEach((sourceName) => {
        const { documents } = augmentationData[sourceName];
        if (documents) {
          documents.map((doc) => {
            const uploaded = doc.metadata?.imported || false;
            let dataRow = {
              sourceName: sourceName,
              selected: uploaded,
              uploaded,
              ...doc,
            };

            setDocumentTblRows((oldVal) => [...oldVal, dataRow]);
          });
        }
      });
    };

    const loadAvailableDocuments = async () => {
      setLoading(true);
      try {
        const response = await getDataAugmentation(
          entity.id,
          availableSources
            .filter((s) => sources.map(({ label }) => label).includes(s.label))
            .map((s) => s.value),
          "documents",
        );
        const { data = {} } = response;
        const { sourcesData = {}, errors } = data || {};
        if (errors && errors.length > 0) {
          throw new Error(errors.join("\n"));
        }
        setAugmentationData(sourcesData);
      } catch (error) {
        showErrorMessage({ error });
      }
      setLoading(false);
    };

    const setPermissions = async () => {
      const { permissions = {} } = entity;

      const canAddAmlDocuments = hasRecordPermission(
        permissions,
        recordTypes.record.actions.addAmlDocument.name,
      );
      setHasAccess(canAddAmlDocuments);

      if (canAddAmlDocuments) {
        await loadAvailableDocuments();
      }
    };

    if (sources) {
      setPermissions();
    }
  }, [entity, sources]);

  useEffect(() => {
    let count = 0;
    documentTblRows.forEach((r) => {
      if (r.selected) {
        count++;
      }
    });

    setIsAllDocsSelected(false);

    if (count === documentTblRows.length) {
      setIsAllDocsSelected(true);
    }
  }, [documentTblRows]);

  const handleSelectAllDocs = (checked) => {
    if (checked) {
      let selectedRows = documentTblRows.map((row) => {
        if (!row.uploaded) {
          row.selected = true;
        }
        return row;
      });
      setDocumentTblRows(selectedRows);
    } else {
      let unSelectedRows = documentTblRows.map((row) => {
        if (!row.uploaded) {
          row.selected = false;
        }
        return row;
      });
      setDocumentTblRows(unSelectedRows);
    }
  };

  const trackSelectedDocs = (checked, doc) => {
    if (!checked) {
      setDocumentTblRows(
        documentTblRows.map((r) => {
          if (r.link === doc.link) {
            r.selected = false;
          }
          return r;
        }),
      );
    } else {
      setDocumentTblRows(
        documentTblRows.map((r) => {
          if (r.link === doc.link) {
            r.selected = true;
          }
          return r;
        }),
      );
    }
  };

  const listPayloadMetadata = (metadata, isValue) => {
    if (!metadata || Object.keys(metadata).length === 0) {
      return [];
    }
    const keysSorted = Object.keys(metadata)
      .filter((k) => k !== "imported")
      .sort((a, b) => a > b);
    if (isValue) {
      return keysSorted.map((key) => metadata[key]);
    }
    return keysSorted;
  };

  const importDocument = async () => {
    try {
      const payload = {
        entityId: entity.id,
        documents: documentTblRows
          .filter((r) => r.selected && !r.metadata.imported)
          .map((d) => ({
            title: d.title,
            link: d.link,
            datasource: d.datasource,
            mimeType: d.mimeType ? d.mimeType : null,
            category: d.category,
            metadata: {
              keys: listPayloadMetadata(d.metadata, false),
              values: listPayloadMetadata(d.metadata, true),
            },
          })),
        action: recordTypes.record.actions.augmentData.name,
      };
      await importExternalDocuments(payload);

      let rows = documentTblRows.map((r) => {
        if (r.selected) {
          r.uploaded = true;
        }
        return r;
      });
      setDocumentTblRows(rows);
      reloadParent(true);
      showMessage({ message: t("messages.record.documents.documentsUpload") });
    } catch (e) {
      showErrorMessage(e);
    }
  };

  const handleDocumentImport = async () => {
    showConfirmation({
      message: t("messages.record.augmentation.documentImportConfirm"),
      onPressYes: async (event) => {
        setIsOperating(true);
        await importDocument();
        setIsOperating(false);
      },
    });
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "30vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const renderButtonLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "20px",
        }}
      >
        <LoadingIndicator size={20} />
      </div>
    );
  };

  const renderDocumentsTableHeader = () => (
    <TableHead>
      <TableRow>
        <TableCell
          scope="col"
          align="center"
          style={{ width: "30px" }}
          className={classes.columnHeader}
        >
          <CheckBox
            size="small"
            color="primary"
            onChange={(e) => handleSelectAllDocs(e.target.checked)}
            checked={isAllDocsSelected}
            style={{ padding: 0, marginRight: 0 }}
          />
        </TableCell>
        {documentTblCols.map((col, ind) => (
          <TableCell
            key={ind}
            scope="col"
            align="center"
            className={classes.columnHeader}
          >
            {col.title}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const renderDocumentsTableBody = () => (
    <TableBody>
      {documentTblRows &&
        documentTblRows.map((row, idx) => (
          <TableRow key={idx}>
            <TableCell align="center" size="small">
              <CheckBox
                size="small"
                color="primary"
                style={{ padding: 0, marginRight: 0 }}
                checked={row.selected}
                disabled={row.uploaded}
                onChange={(e) => trackSelectedDocs(e.target.checked, row)}
                className={classes.fieldValue}
              />
            </TableCell>
            {documentTblCols.map((col, ix) => (
              <TableCell
                key={ix}
                className={classes.fieldValue}
                align="center"
                size="small"
              >
                {row[col.field]}
              </TableCell>
            ))}
          </TableRow>
        ))}
    </TableBody>
  );

  const renderDocumentsTable = () => {
    return (
      <Card>
        <Table className={classes.table} size="small">
          {renderDocumentsTableHeader()}
          {renderDocumentsTableBody()}
        </Table>
        <CardActions className={classes.cardActionButton}>
          <LightTooltip
            title={
              <span>{t("messages.record.augmentation.documentHelp")}</span>
            }
            placement="left-start"
            style={{ padding: "0.5rem" }}
          >
            <span>
              <Button
                className={classes.sectionButton}
                label={t("labels.common.save")}
                aria-label={t("labels.common.save")}
                onClick={handleDocumentImport}
                disableElevation
                disabled={
                  isOperating ||
                  documentTblRows.filter((d) => d.selected && !d.uploaded)
                    .length === 0
                }
                size="small"
                color="primary"
              >
                {isOperating ? (
                  renderButtonLoadingIndicator()
                ) : (
                  <>{t("labels.common.save")}</>
                )}
              </Button>
            </span>
          </LightTooltip>
        </CardActions>
      </Card>
    );
  };

  return hasAccess ? (
    loading ? (
      renderLoadingIndicator()
    ) : (
      renderDocumentsTable()
    )
  ) : (
    <div style={{ position: "relative" }}>
      <NoAccessScreen />
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(DocumentsTable);
